import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useInitializeOrgFunctions } from '@/api/mutations/useInitializeOrgFunctions';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import { serializeRowData } from './utils/classifyAnchorDrafts';
import { membershipDuplicateKeys, orgFunctionDuplicateKeys } from './utils/draftRowValidation';
import { orgFunctionsToDrafts } from './utils/existingToDrafts';
import { validateDraftRows } from '@/lib/draft-rows/markDuplicateRows';
import {
  commitOrgFunctionSurface,
  pendingEntityRemovals,
  pendingMembershipRemovals
} from './utils/orgFunctionSurfaceWire';
import { buildOrgFunctionIdLookup } from './utils/orgFunctionSurfacePolicies';
import type { OrgFunctionSurfaceDrafts } from './steps/OrgFunctionSurfaceStep';
import type { WizardValidationContext } from './useWizardValidationContext';
import { runStepMutation } from './stepSubmit';

interface UseOrgFunctionSurfaceSubmitArgs {
  validation: WizardValidationContext;
  setSubmitError: (error: string | undefined) => void;
}

/**
 * Weiter handler shared by the Funktionen and Teams steps. Both surface
 * steps share the endpoint (kind-by-surface stays on the wire — ADR 0010):
 * each step fills exactly one of the two arrays. Rows autosave created
 * silently already carry their entityId (stamped from the flush response),
 * so they send `_id` and update in place instead of duplicating.
 */
export function useOrgFunctionSurfaceSubmit({
  validation,
  setSubmitError
}: UseOrgFunctionSurfaceSubmitArgs) {
  const { t } = useTranslation();
  const initializeOrgFunctions = useInitializeOrgFunctions();
  // Same queries the surface steps seed their tables from, so the unchanged
  // check below compares against exactly the rows the user saw.
  const { data: existingFunctions } = useOrgFunctions('role');
  const { data: existingTeams } = useOrgFunctions('team');

  const submitOrgFunctionSurface = useCallback(
    (
      kind: 'role' | 'team',
      surfaceDrafts: OrgFunctionSurfaceDrafts,
      setSurfaceDrafts: (drafts: OrgFunctionSurfaceDrafts) => void,
      navigateNext: () => void
    ): Promise<void> =>
      runStepMutation<OrgFunctionSurfaceDrafts>({
        isEmpty:
          surfaceDrafts.rows.length +
            surfaceDrafts.memberships.length +
            surfaceDrafts.removedEntityIds.length +
            surfaceDrafts.membershipRemovals.length ===
          0,
        // This surface validates two arrays; reflect the marked rows and
        // pass the validated pair (plus the untouched pending removals)
        // through when both are clean.
        validate: () => {
          const rows = validateDraftRows(
            surfaceDrafts.rows,
            validation.validateOrgFunctionRow,
            orgFunctionDuplicateKeys
          );
          const memberships = validateDraftRows(
            surfaceDrafts.memberships,
            validation.validateMembershipRow,
            membershipDuplicateKeys
          );
          setSurfaceDrafts({ ...surfaceDrafts, rows: rows.rows, memberships: memberships.rows });
          return rows.allValid && memberships.allValid
            ? { ...surfaceDrafts, rows: rows.rows, memberships: memberships.rows }
            : null;
        },
        mutate: async ({ rows, memberships, removedEntityIds, membershipRemovals }) => {
          const existingOrgFunctions = (kind === 'role' ? existingFunctions : existingTeams) ?? [];
          // A seeded row that still matches its server copy verbatim must not
          // be sent: `_id` updates are undiffed on this surface, and the
          // backend $unsets fields the payload leaves blank — an untouched
          // captured seed (or a verbatim re-upload) would otherwise fire a
          // destructive no-op update.
          const seedDataById = new Map(
            orgFunctionsToDrafts(existingOrgFunctions).map((seed) => [
              seed.entityId,
              serializeRowData(seed.data)
            ])
          );
          const changedRows = rows.filter(
            (row) => !row.entityId || seedDataById.get(row.entityId) !== serializeRowData(row.data)
          );
          const { createdCount, membershipsLinked } = await commitOrgFunctionSurface({
            kind,
            rows: changedRows,
            memberships,
            departments: validation.departmentsList,
            // Deleted persisted rows / unlinked flushed memberships queue in
            // the draft and commit here — removals are Weiter-only, exactly
            // like the anchor steps.
            removals: {
              entityIds: pendingEntityRemovals(removedEntityIds, rows),
              memberships: pendingMembershipRemovals(
                membershipRemovals,
                memberships,
                buildOrgFunctionIdLookup(rows, existingOrgFunctions)
              )
            },
            mutate: initializeOrgFunctions.mutateAsync
          });
          toast.success(
            t('initialSetup.functionsTeams.toast.createSuccess', {
              created: createdCount,
              linked: membershipsLinked
            })
          );
          // Drop the captured drafts (like the anchor steps) — they are now
          // persisted, so a revisit re-seeds from the refetched OrgFunctions.
          // Without this, the stale snapshot would shadow later changes (e.g. a
          // Stelle rename that mints a new role function on the Mitarbeiter step).
          setSurfaceDrafts({
            rows: [],
            memberships: [],
            removedEntityIds: [],
            membershipRemovals: []
          });
        },
        navigateNext,
        fixErrorsToastKey: 'initialSetup.functionsTeams.toast.fixErrors',
        failedToastKey: 'initialSetup.functionsTeams.toast.createFailed',
        t,
        setSubmitError
      }),
    [existingFunctions, existingTeams, initializeOrgFunctions, setSubmitError, t, validation]
  );

  return {
    submitOrgFunctionSurface,
    isOrgFunctionSurfacePending: initializeOrgFunctions.isPending
  };
}
